/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar, SNACKBAR_TYPE } from '@panel/commons/lib/snackbar';
import { Icon, useBreakpoint } from '@panel/commons';
import { getColor } from '@panel/commons/core/themev3';
import { isMobileDevice } from '@panel/commons/helpers/device';
import {
  backendForDND,
  MAX_FILE_HEIGHT,
  MAX_FILE_WIDTH,
  MIN_FILE_HEIGHT,
  MIN_FILE_WIDTH,
  MAX_FILES,
  MAX_FILE_SIZE,
  MIN_FILE_SIZE,
  createPicture
} from './helpers';
import { ItemList } from './itemlist';
import {
  SubtitleHelper,
  SectionContainer,
  FilesContainer,
  DropzoneContainer,
  CollapseCheck,
  CollapseContainer,
  CollapseWrapper
} from './styles';
import { resolveImageDimensions } from './images';
import { ModuleWrapper } from '../../../Wrappers/ModuleWrapper';
import {
  getPlans,
  addMultimedia,
  updateMultimedia,
  deleteMultimedia,
  moveMultimedia,
  MultimediaType,
  MultimediaCollection,
  addErrorMultimedia
} from '../../../../ducks/step2';
import { setProcessingMultimedia } from '../../../../ducks/step2/actions';

const dropZoneConfig = Object.freeze({
  accept: 'image/*',
  multiple: true
});

const keysErrors = Object.freeze({
  format: {
    title: 'publisher.step2.module2.plans.error.format.title',
    subtitle: 'publisher.step2.module2.plans.error.format.subtitle'
  },
  sizeMin: {
    title: 'publisher.step2.module2.plans.error.size.min.title',
    subtitle: 'publisher.step2.module2.plans.error.size.min.title'
  },
  sizeMax: {
    title: 'publisher.step2.module2.plans.error.size.max.title',
    subtitle: 'publisher.step2.module2.plans.error.size.max.subtitle'
  },
  dimensionsMin: {
    title: 'publisher.step2.module2.plans.error.dimensions.min.title',
    subtitle: 'publisher.step2.module2.plans.error.dimensions.min.subtitle'
  },
  dimensionsMax: {
    title: 'publisher.step2.module2.plans.error.dimensions.max.title',
    subtitle: 'publisher.step2.module2.plans.error.dimensions.max.subtitle'
  },
  generic: {
    title: 'publisher.step2.module2.plans.error.generic',
    subtitle: '.'
  }
});

const PlansSection = () => {
  const [collapse, setCollapse] = useState({ isCollapsed: false, keepOpen: false });
  const { plans } = useSelector(getPlans);
  const { setToggleSnack, setSnackBar } = useSnackbar();
  const [reachedBreakPointDesktop] = useBreakpoint(1280);
  const isMobile = isMobileDevice();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    const n = !isMobile ? 3 : 5;
    setCollapse({ ...collapse, isCollapsed: plans.length > n && !collapse.keepOpen });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, isMobile]);

  const fireSnack = text => {
    setSnackBar(text, SNACKBAR_TYPE.DANGER);
    setToggleSnack(10000);
  };

  const fileDispatcher = (files = []) => {
    const take = MAX_FILES - plans.length;
    if (take > 0 && files.length < take) {
      files.forEach(file => {
        const picture = createPicture(file, MultimediaType.PLAN);
        dispatch(addMultimedia(picture, MultimediaCollection.PLAN));
      });
    } else if (take > 0 && files.length >= take) {
      const filesToAccept = files.slice(0, take);
      filesToAccept.forEach(file => {
        const picture = createPicture(file, MultimediaType.PLAN);
        dispatch(addMultimedia(picture, MultimediaCollection.PLAN));
      });
      if (files.length > take) {
        fireSnack(t('publisher.step2.module2.plans.validation.max'));
      }
    } else {
      fireSnack(t('publisher.step2.module2.plans.validation.max'));
    }
  };

  const handleImageErrors = (results = []) => {
    const files = [];

    results.forEach(result => {
      if (result && Object.prototype.hasOwnProperty.call(result, 'dimensions')) {
        if (result.dimensions.width > MAX_FILE_WIDTH || result.dimensions.height > MAX_FILE_HEIGHT) {
          result.file.error = keysErrors.dimensionsMax;
        }
        if (result.dimensions.width < MIN_FILE_WIDTH || result.dimensions.height < MIN_FILE_HEIGHT) {
          result.file.error = keysErrors.dimensionsMin;
        }
      }
      if (result && !result.file.error) {
        if (result.file.size > MAX_FILE_SIZE) {
          result.file.error = keysErrors.sizeMax;
        }
        if (result.file.size < MIN_FILE_SIZE) {
          result.file.error = keysErrors.sizeMin;
        }
      }
      files.push(result.file);
    });
    if (files.find(f => f.error)) {
      fireSnack(t('publisher.step2.module2.plans.validationErrorLoading'));
    }
    fileDispatcher(files);
  };

  const validateDimensions = async files => {
    const filesQueue = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < files.length; i++) {
      const { type } = files[i];
      if (type === 'image/jpg' || type === 'image/jpeg' || type === 'image/png') {
        // eslint-disable-next-line no-await-in-loop
        const result = await resolveImageDimensions(files[i]);
        filesQueue.push(result);
      } else {
        files[i].error = keysErrors.format;
        filesQueue.push({ file: files[i] });
      }
    }
    handleImageErrors(filesQueue);
  };

  const onDrop = acceptedFiles => {
    if (acceptedFiles.length > 0) {
      validateDimensions(acceptedFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...dropZoneConfig,
    onDrop
  });

  const onDelete = id => {
    dispatch(deleteMultimedia(id, MultimediaCollection.PLAN));
  };

  const onUpdate = multimedia => {
    dispatch(updateMultimedia(multimedia, MultimediaCollection.PLAN));
  };

  const onMove = (dragIndex, hoverIndex) => {
    dispatch(moveMultimedia(dragIndex, hoverIndex, MultimediaCollection.PLAN));
  };

  const onError = (id, keyError = keysErrors.generic.title) => {
    dispatch(addErrorMultimedia(id, MultimediaCollection.PLAN, keyError));
    fireSnack(t('publisher.step2.module2.plans.validationErrorLoading'));
  };

  const getMaxHeight = () => {
    const minImages = isMobile ? 1 : 2;
    const divisor = isMobile ? 2 : 3;
    return plans.length > minImages ? Math.ceil(plans.length / divisor) * 245 + 245 : 250;
  };

  const onStatusChange = useCallback(({ key, status }) => {
    if (key) {
      dispatch(setProcessingMultimedia({ key, status }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ModuleWrapper title={t('publisher.step2.module2.plans.title')}>
        <SubtitleHelper data-repa='step2-plans-subtitle'>{t('publisher.step2.module2.plans.text')}</SubtitleHelper>
        <SectionContainer data-repa='step2-plans-sectionContainer'>
          <DndProvider backend={backendForDND}>
            <CollapseWrapper data-repa='step2-plans-collapsableWrapper' hasItems={plans.length > 0}>
              <CollapseContainer
                data-repa='step2-plans-collapsableContainer'
                maxHeight={getMaxHeight()}
                isCollapsed={collapse.isCollapsed}
              >
                <FilesContainer data-repa='step2-plans-fileContainer' isCollapsed={collapse.isCollapsed}>
                  <DropzoneContainer data-repa='step2-plans-dropzone' {...getRootProps()} hasItems={plans.length > 0}>
                    <input data-repa='step2-plans-input' {...getInputProps()} />
                    {!isDragActive && (
                      <>
                        <Icon
                          name='upload'
                          width={30}
                          height={30}
                          viewBox='0 0 46 46'
                          colorFill={getColor('primary')}
                        />
                        <span data-repa='step2-plans-input-label'>
                          {t(`publisher.step2.module2.plans.placeholder.${reachedBreakPointDesktop ? 'd' : 'm'}`)}
                        </span>
                      </>
                    )}
                  </DropzoneContainer>
                  <ItemList
                    data-repa='step2-plans-itemlist'
                    items={plans}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onMove={onMove}
                    onError={onError}
                    onStatusChange={onStatusChange}
                    editablePlaceholder={t('publisher.step2.module2.plans.placeholderPictureFooter')}
                    keysErrors={keysErrors}
                    setCollapse={setCollapse}
                    collapse={collapse}
                    photoType='plans'
                  />
                </FilesContainer>
              </CollapseContainer>
              {!collapse.isCollapsed && (
                <CollapseCheck
                  data-repa='step2-plans-collapseCheck'
                  showCollapse={plans.length > (isMobile ? 3 : 5)}
                  onClick={() => setCollapse({ isCollapsed: !collapse.isCollapsed, keepOpen: !collapse.keepOpen })}
                >
                  <div data-repa='step2-plans-collapsableCheck-label'>
                    {t('publisher.step2.module2.plans.btn.less')}
                  </div>
                  <Icon
                    data-repa='step2-plans-collapsableCheck-icon'
                    name='chevronUp'
                    width={16}
                    height={16}
                    colorFill={getColor('primary')}
                    viewBox='0 0 16 16'
                  />
                </CollapseCheck>
              )}
            </CollapseWrapper>
          </DndProvider>
        </SectionContainer>
      </ModuleWrapper>
    </>
  );
};

export { PlansSection };
